import { action, makeObservable, observable } from 'mobx';
import { AuthenticateByCodeStore } from './authenticateByCode';
import { RootStore } from './root';
import {
  loginTwoFactorAuthenticationSendCodeByPhoneClick,
  loginTwoFactorAuthenticationSendCodeByEmailClickSrc6Evid175,
  loginTwoFactorAuthenticationLoginWithAuthenticatorAppClick,
  loginClickOnBack,
} from '@wix/bi-logger-hls2/v2';
import { loginTwoFactorAuthenticationLoginWithOwnerAppClick } from '@wix/bi-logger-identity-data/v2';
import { TwoFactorAuthMethod, TwoFactorAuthMethods } from '../utils/constants';
import { ApprovalChallengeStatus } from './premiumUsers2FaEnforcement/ownerApp/types';
import { extractErrorKeyByErrorCode } from '../utils/errorHandler';

export class EnterCodeStore {
  private readonly rootStore: RootStore;

  rememberMe: boolean = true;
  isSwitchingMethod: boolean = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      rememberMe: observable,
      isSwitchingMethod: observable,
      onSwitchMethod: action,
      onToggleRememberThisDevice: action,
    });
  }

  onToggleRememberThisDevice() {
    this.rememberMe = !this.rememberMe;
  }

  async onSwitchMethod(
    newMethod: TwoFactorAuthMethod,
    authStore: AuthenticateByCodeStore,
  ) {
    // Prevent multiple simultaneous switch attempts
    if (this.isSwitchingMethod) {
      return;
    }

    const biEvent = {
      [TwoFactorAuthMethods.Email]:
        loginTwoFactorAuthenticationSendCodeByPhoneClick,
      [TwoFactorAuthMethods.Phone]:
        loginTwoFactorAuthenticationSendCodeByEmailClickSrc6Evid175,
      [TwoFactorAuthMethods.TOTP]:
        loginTwoFactorAuthenticationLoginWithAuthenticatorAppClick,
      [TwoFactorAuthMethods.Push]:
        loginTwoFactorAuthenticationLoginWithOwnerAppClick,
    };
    biEvent[newMethod] &&
      this.rootStore.biLogger.report(
        biEvent[newMethod]({ origin: authStore.currentTwoFAMethod }),
      );

    // Clear any previous notification and set loading state
    this.rootStore.notificationStore.close();
    this.isSwitchingMethod = true;

    try {
      if (authStore.switchToMethod) {
        // Atomic switch - API call first, then state update on success
        await authStore.switchToMethod(newMethod);
      } else {
        // Fallback for stores that don't support atomic switch
        authStore.currentTwoFAMethod = newMethod;
        await authStore.resendCode();
      }

      // Switch succeeded - clear the code field for the new method
      authStore.clear();

      // Handle Push method special case
      if (newMethod === TwoFactorAuthMethods.Push) {
        this.rootStore.twoFactorAuthStore.setTwoFactorAuthStatus(
          ApprovalChallengeStatus.PENDING_APPROVAL,
        );
      }
    } catch (error) {
      // Switch failed - stay on current method and show error toast with auto-hide
      const errorCode = error instanceof Error ? error.message : String(error);
      const errorKey = extractErrorKeyByErrorCode(errorCode);
      if (errorKey) {
        this.rootStore.notificationStore.openErrorToast(
          this.rootStore.i18n.t(errorKey),
          3000,
        );
      }
    } finally {
      this.isSwitchingMethod = false;
    }
  }

  onBackButtonClicked(method: string) {
    this.rootStore.biLogger.report(
      loginClickOnBack({ step_name: 'verify_code', flow_type: method }),
    );
    this.rootStore.navigationStore.goBack();
  }
}
